/** FHIR tag systems used on DocumentReference for provenance in PHC. */
export const FHIR_DOCUMENT_TYPE_TAG_SYSTEM =
  'http://lifeomic.com/fhir/document-type';
export const FHIR_SOURCE_TYPE_TAG_SYSTEM =
  'http://lifeomic.com/fhir/source-type';

export const MEMBER_UPLOADED_FILE_DOCUMENT_TYPE = 'member-uploaded-file';
export const CLINICIAN_PROVIDED_MEMBER_FILE_SOURCE_TYPE =
  'clinician-provided-member-file';
export const DOCUMENT_EXTRACTION_V2_SUMMARY_DOCUMENT_TYPE =
  'document-extraction-v2-summary';
export const DOCUMENT_EXTRACTION_V2_SUMMARY_SOURCE_TYPE = 'zori-analysis';

function isDocumentReference(
  resource: fhir.Resource | null | undefined,
): resource is fhir.DocumentReference {
  return resource?.resourceType === 'DocumentReference';
}

/**
 * Returns true when a DocumentReference represents a member-uploaded file
 * (as opposed to clinician or system sources). Used to gate delete in PHC.
 *
 * Member uploads are identified by the document-type tag surfaced in the
 * member document table's Type column.
 */
export function isMemberUploadedPatientDocument(
  resource: fhir.Resource | null | undefined,
): boolean {
  if (!isDocumentReference(resource)) {
    return false;
  }

  return (
    resource.meta?.tag?.some(
      tag =>
        tag.system === FHIR_DOCUMENT_TYPE_TAG_SYSTEM &&
        tag.code === MEMBER_UPLOADED_FILE_DOCUMENT_TYPE,
    ) ?? false
  );
}

export function isClinicianProvidedMemberFileSourceDocument(
  resource: fhir.Resource | null | undefined,
): boolean {
  if (!isDocumentReference(resource)) {
    return false;
  }

  return (
    resource.meta?.tag?.some(
      tag =>
        tag.system === FHIR_SOURCE_TYPE_TAG_SYSTEM &&
        tag.code === CLINICIAN_PROVIDED_MEMBER_FILE_SOURCE_TYPE,
    ) ?? false
  );
}

export function canDeleteMemberDocument(
  resource: fhir.Resource | null | undefined,
  options: { canDeleteClinicianProvidedMemberFiles: boolean },
): boolean {
  return (
    isMemberUploadedPatientDocument(resource) ||
    (options.canDeleteClinicianProvidedMemberFiles &&
      isClinicianProvidedMemberFileSourceDocument(resource))
  );
}

export function isDocumentExtractionV2SummaryDocument(
  resource: fhir.Resource | null | undefined,
): boolean {
  if (!isDocumentReference(resource)) {
    return false;
  }

  const hasSummaryTypeCoding =
    resource.type?.coding?.some(
      coding =>
        coding.system === FHIR_DOCUMENT_TYPE_TAG_SYSTEM &&
        coding.code === DOCUMENT_EXTRACTION_V2_SUMMARY_DOCUMENT_TYPE,
    ) ?? false;
  const hasSummaryDocumentTypeTag =
    resource.meta?.tag?.some(
      tag =>
        tag.system === FHIR_DOCUMENT_TYPE_TAG_SYSTEM &&
        tag.code === DOCUMENT_EXTRACTION_V2_SUMMARY_DOCUMENT_TYPE,
    ) ?? false;
  const hasSummarySourceTypeTag =
    resource.meta?.tag?.some(
      tag =>
        tag.system === FHIR_SOURCE_TYPE_TAG_SYSTEM &&
        tag.code === DOCUMENT_EXTRACTION_V2_SUMMARY_SOURCE_TYPE,
    ) ?? false;

  return (
    hasSummaryTypeCoding || hasSummaryDocumentTypeTag || hasSummarySourceTypeTag
  );
}

export function shouldRenderInMemberDocumentsTable(
  resource: fhir.Resource,
): boolean {
  return !isDocumentExtractionV2SummaryDocument(resource);
}
